import { useState, useEffect } from 'react';
import { getBusinessById, addSeats } from '../../services/businessService';

interface ManageSeatsModalProps {
  businessId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function ManageSeatsModal({ businessId, onClose, onSuccess }: ManageSeatsModalProps) {
  const [currentSeats, setCurrentSeats] = useState<number | null>(null);
  const [totalSeats, setTotalSeats] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getBusinessById(businessId, 'expand=activePlan')
      .then((business) => {
        const seats = business.activePlan?.totalSeats ?? 0;
        setCurrentSeats(seats);
        setTotalSeats(seats);
      })
      .catch(() => setError('Failed to load current seats'))
      .finally(() => setFetching(false));
  }, [businessId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (currentSeats !== null && totalSeats <= currentSeats) return;

    setError('');
    setLoading(true);
    try {
      await addSeats(businessId, totalSeats);
      onSuccess();
    } catch {
      setError('Failed to update seats');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
        <form onSubmit={handleSubmit}>
          <div className="px-6 pt-6 pb-4">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Manage Seats</h2>
            {fetching ? (
              <p className="text-sm text-gray-400">Loading...</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Current Seats</label>
                  <p className="text-sm text-gray-900">{currentSeats}</p>
                </div>
                <div>
                  <label className={labelClass}>New Total Seats *</label>
                  <input
                    type="number"
                    value={totalSeats}
                    onChange={(e) => setTotalSeats(Number(e.target.value))}
                    min={(currentSeats ?? 0) + 1}
                    required
                    className={inputClass}
                  />
                </div>
              </div>
            )}
            {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || fetching || currentSeats === null || totalSeats <= currentSeats}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Updating...' : 'Update Seats'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
